/**
 * Koden her kan kanskje flyttes slik at den er en del av d-application.ts eller ligger parallelt med den.
 */
import type { ApplicationViewModel, LoadedApplicationViewModel } from 'src/layout/application-view-model.js';
import { currentContentViews } from 'src/models/content/content-view-factory.js';
import { pageView } from 'src/models/pages/page-view.js';
import { sortedByName } from 'src/store/utilities.js';
import { DayOfWeek, LocalDate } from 'src/utilities/local-date.js';
import type { OrganizationState, State } from '../store/types.js';
import { isTutorialActive } from 'src/models/tutorials.js';
import { currentTutorialContent } from 'src/store/selectors/current-tutorial-content.js';
import { availableTutorialsSelector } from 'src/store/selectors/available-tutorials.js';
import { currentEntityTypeName, getCurrentPath, splitPath } from 'src/store/selectors/path.js';
import { currentRobotAlert, functionsByTemplateId, showRobotAlert } from 'src/store/selectors/robot-alert.js';
import { breadCrumbSelectedName } from 'src/store/selectors/breadcrumbs.js';
import { itemsByTypeAndId, loaded } from 'src/store/selectors/selectors';
import { type ApiClient, createClient } from 'src/store/backend';
import {
  currentEmployeeForStaffingCalendar,
  currentUserHasStaffingCalendarAccess,
  currentWeekRemainingTasksAndMeetingsForEmployee,
  getContactsAsUsers,
  getEmployeesWithAccessForAdministrator,
  getItemDataFromTemplateId,
  getRecipientOptionsForIssues,
} from 'src/store/selectors';
import { convertInternalLinks } from 'src/models/internal-linking.js';
import { docsForLinkingById } from 'src/store/selectors/linking/docsForLinkingById';
import { uiSettings } from 'src/store/selectors/ui';
import { sortBy } from 'lodash';
import type { EmployeeViewModel } from '@trinnvis/dabih-api-client';
import { requiresSecureLogin } from 'src/store/selectors';
import { displayedUserName } from 'src/store/selectors';
import { getCurrentUserEmail } from 'src/store/selectors';
import { currentEmployeeUuid } from 'src/store/selectors';
import { currentEmployeeAsViewModel } from 'src/store/selectors';
import { isCurrentUserEmployee } from 'src/store/selectors';
import { employeesNotDeleted } from 'src/store/selectors';
import { employeesNotTerminatedCount } from 'src/store/selectors';
import { employeesDeleted } from 'src/store/selectors';
import { getOrganization } from 'src/store/selectors';
import { getOrganizationId } from 'src/store/selectors';
import { singleUserVersion } from 'src/store/selectors';
import { partnersNotDeleted } from 'src/store/selectors';
import { functionsNotDeleted } from 'src/store/selectors';

import { startTasks } from 'src/store/selectors';
import type { SideContent } from '../store/selectors/side-content';
import type { PageGroup } from 'src/store/selectors';
import type { MobileAppViewModel } from '../outskirts/account/mobile-app/mobile-app-view-model';
import type { SettingsItem } from '../layout/parts/settings-item';
import type { PageMenuGroup } from '../layout/parts/page-menu-group';
import type { StartTask } from '../layout/parts/start-task';
import { defaultPageId } from '../store/selectors/pages/defaultPageId';
import { getPageGroups } from '../store/selectors/pages/getPageGroups';
import { translateTemplateName } from 'src/i18n/template-content';
import { getSpecialTerms } from '../store/selectors/features/getSpecialTerms';
import { getFeatureStates } from '../store/selectors/features/getFeatureStates';
import { writeAccess } from '../store/selectors/user/writeAccess';
import { getUsersForAccess } from './get-users-for-access';
import { isPageActive } from 'src/store/selectors/features/isPageActive';
import { getPromoCodeForPage, menuPagesWithPromo } from 'src/store/selectors/promo';
import type { FeatureStates } from 'src/store/selectors/featureStates';
import { organizationName } from 'src/store/selectors/organization/organizationName.js';
import { lastOrganizationsLoadedByUser } from 'src/store/selectors/user/lastOrganizationsLoadedByUser';
import { isUserEmergencyMode } from 'src/store/selectors/is-user-emergency-mode';
import { trace } from 'src/utilities/trace';
import { emergencyAccess } from 'src/store/selectors/emergency/emergency-access';
import { emergencyMeasuresCardsPublished } from 'src/store/selectors/emergency/emergency-measures-cards-published';
import type { EmergencyViewModel } from 'src/outskirts/account/emergency/emergency-view-model';
import {
  getContactsWithContactInfo,
  getEmergencyPartnersAndContacts,
  getEmployeesWithContactInfo,
  getPartnersAndContactsWithContactInfo,
  getPartnersWithContactInfo,
} from 'src/store/selectors/emergency/emergency-contacts';
import { emergencyMeasuresCards } from 'src/store/selectors/emergency/emergency-measures-cards';

/**
 * Checks if the user has only performanceReview module active and no other features.
 * PerformanceReview is a standalone module that can work independently.
 */
export function isPerformanceReviewOnly(featureStates: FeatureStates): boolean {
  // List of all main features that are modules (as defined in MainFeatureStates)
  const otherMainFeatures = ['core', 'meetings', 'hse', 'staffing', 'assets', 'substances', 'issues', 'risk', 'data'];

  // Check if performanceReview is active and all other main features are inactive
  return featureStates.performanceReview && !otherMainFeatures.some((feature) => featureStates[feature]);
}

function getPageMenu(
  pageGroups: PageGroup[],
  organizationId: number,
  featureStates: FeatureStates,
  singleUserVersion: boolean,
): PageMenuGroup[] {
  // Theme pages the server returned but the user can't actually access — show greyed out at the bottom
  const inactiveThemePages: { pageId: number; pageName: string }[] = [];

  const result: PageMenuGroup[] = pageGroups.map((group) => {
    let name = '';
    if (group.code === 'current' || group.code === 'themePages' || group.code === 'organization') {
      name = translateTemplateName(group.name);
    }
    const pages =
      group.code === 'themePages'
        ? group.pages.filter((page) => {
            if (!isPageActive(page.pageId, featureStates, singleUserVersion)) {
              inactiveThemePages.push({ pageId: page.pageId, pageName: page.name });
              return false;
            }
            return true;
          })
        : group.pages;
    return {
      code: group.code,
      name,
      pages: pages.map((page) => {
        return {
          pageId: page.pageId,
          name: translateTemplateName(page.name),
          href: '/account/' + organizationId + '/' + page.pageId,
          promo: '',
        };
      }),
    };
  });
  const activePageIds = result.flatMap((g) => g.pages.map((p) => p.pageId));
  const seenPageIds = new Set<number>();
  const promoPages = [...menuPagesWithPromo, ...inactiveThemePages]
    .filter((p) => !activePageIds.includes(p.pageId))
    .filter((p) => {
      if (seenPageIds.has(p.pageId)) return false;
      seenPageIds.add(p.pageId);
      return true;
    });
  if (promoPages.length) {
    result.push({
      code: 'promo',
      name: '',
      pages: promoPages.map((page) => {
        return {
          pageId: page.pageId,
          name: page.pageName,
          href: '',
          promo: getPromoCodeForPage(page.pageId),
        };
      }),
    });
  }
  return result;
}

function getSettings(
  state: State,
  currentEmployee: EmployeeViewModel | undefined,
  currentUserIsOwner: boolean,
  organization: OrganizationState,
): SettingsItem[] {
  const api = createClient();
  const result: SettingsItem[] = [];
  result.push({
    name: 'Brukervalg',
    id: 'profile',
    type: 'profile',
    isEmployee: isCurrentUserEmployee(state),
    user: {
      criiptoAuthDescription: state.user?.criiptoAuthDescription ?? '',
      authDescription: state.user?.authDescription ?? '',
      token: '',
      username: state.user?.username ?? '',
    },
    reminders: currentEmployee?.receiveReminders ?? 'NONE',
    alerts: currentEmployee?.summaries ?? 'NONE',
    features: state.organization?.features ?? [],
    ownerEmail: state.organization?.ownerEmail ?? '',
  });
  if (currentUserIsOwner) {
    result.push({
      name: 'Valg for organisasjon',
      id: 'subscription',
      type: 'subscription',
      singleUserVersionAvailable: _singleUserVersionAvailable(state),
      administratorEmployeesList: getEmployeesWithAccessForAdministrator(state).map((a) => ({
        name: a.name,
        email: a.email ?? '',
      })),
      subscription: {
        invoiceAddress: organization?.invoiceAddress ?? '',
        invoiceLocality: organization?.invoiceLocality ?? '',
        invoiceOrganizationNumber: organization?.invoiceOrganizationNumber ?? '',
        invoicePostcode: organization?.invoicePostcode ?? '',
        invoiceReceiver: organization?.invoiceReceiver ?? '',
        invoiceReference: organization?.invoiceReference ?? '',
        invoiceSendMethod: organization?.invoiceSendMethod === 'EHF' ? 'EHF' : 'MANUAL',
        ownerEmail: organization?.ownerEmail ?? '',
        specialTerms: organization?.specialTerms ?? 'NONE',
        singleUser: singleUserVersion(state),
        requiresSecureLogin: requiresSecureLogin(state) ?? false,
      },
      elmaLookup: async (q) => {
        const p = await api.accounts.searchElmaRegistry({ query: q });
        return p.map((x) => ({ value: x.identifier, text: x.identifier + ' ' + x.name }));
      },
    });
    result.push({
      name: 'Brukere',
      id: 'users',
      type: 'users',
      users: getUsersForAccess(state),
      ownerEmail: organization?.ownerEmail ?? '',
      currentUserEmail: state.user?.username ?? '',
    });
  }
  result.push({
    name: 'Organisasjoner',
    id: 'organizations',
    type: 'organizations',
    organizations: sortedByName(state.user?.organizations ?? []),
  });
  return result;
}

function getMobileAppViewModel(state: State): MobileAppViewModel {
  const organization = getOrganization(state);
  if (organization === undefined) {
    throw new Error('Illegal state (E153), organization not found');
  }
  const featureStates = getFeatureStates(state);
  return {
    currentOrganizationId: getOrganizationId(state),
    currentEmployeeUuid: currentEmployeeUuid(state) ?? '',
    currentPath: getCurrentPath(state),
    employeeForStaffing: currentEmployeeForStaffingCalendar(state),
    organizations: sortBy(state.user?.organizations ?? [], [(item) => item.name.toLowerCase()]),
    recipientOptionsForIssues: getRecipientOptionsForIssues(state),
    currentUserHasStaffingAccess: currentUserHasStaffingCalendarAccess(state),
    leavePeriodEditRestriction: organization.leavePeriodEditRestriction,
    eventsCount: currentWeekRemainingTasksAndMeetingsForEmployee(state),
    isPerformanceReviewOnly: isPerformanceReviewOnly(featureStates),
    featureStates,
  };
}

export function getEmergencyViewModel(state: State): EmergencyViewModel {
  return {
    organizationName: getOrganization(state)?.name ?? '',
    emergencyContacts: getEmergencyPartnersAndContacts(state),
    employees: getEmployeesWithContactInfo(state),
    partners: getPartnersWithContactInfo(state),
    contacts: getContactsWithContactInfo(state),
    partnersAndContacts: getPartnersAndContactsWithContactInfo(state),
    measuresCards: emergencyMeasuresCards(state),
  };
}

function getCurrentSideContent(state: State): SideContent | undefined {
  if (state.currentSideContentTemplateId && state.currentSideContentTemplateId !== 0) {
    const items = itemsByTypeAndId(state);
    if (items) {
      const item = getItemDataFromTemplateId(state.currentSideContentTemplateId + '', items, singleUserVersion(state));
      if (item) {
        let content = item.content;
        const docsIndex = docsForLinkingById(state);
        const converted = convertInternalLinks(item.content, docsIndex);
        if (converted) {
          content = converted;
        }
        return { ...item, content };
      }
    }
  }
  return undefined;
}

export async function loadedApplicationViewModel(api: ApiClient, state: State): Promise<LoadedApplicationViewModel> {
  return trace('appViewModel.loaded', () => loadedApplicationViewModelInner(api, state));
}

async function loadedApplicationViewModelInner(api: ApiClient, state: State): Promise<LoadedApplicationViewModel> {
  const pages = getPageGroups(state);
  const organizationId = getOrganizationId(state);
  const singleUser = singleUserVersion(state);
  const pathSegments = splitPath(state).slice(3);
  const currentEmployee = currentEmployeeAsViewModel(state);
  const organization = getOrganization(state);
  const currentPageId = pathSegments.length > 0 ? Number(pathSegments[0] ?? '' + defaultPageId) : defaultPageId;
  const hrefPrefix = '/account/' + organizationId + '/' + currentPageId + '/';
  const now = LocalDate.fromString(state.today);
  const monday = now.withPreviousOrSame(DayOfWeek.MONDAY);

  function currentStartTask(state: State): StartTask | undefined {
    const featureStates = getFeatureStates(state);
    if (!featureStates.core && !featureStates.hse) {
      return undefined;
    }
    const tasks = startTasks(state);
    const predicate =
      state.selectedStartTask !== '' ? (t) => t.uuid === state.selectedStartTask : (t) => t.myTask && !t.executed;

    const docsIndex = docsForLinkingById(state);
    return tasks
      .filter(predicate)
      .slice(0, 1)
      .map((t) => ({
        uuid: t.uuid,
        weeksFromNow: Math.floor(monday.until(t.time) / 7),
        weekDisplay: 'Uke ' + t.time.toStringForDisplayWeekAndYear(),
        functionName: t.functionName,
        href: t.href,
        name: t.name,
        helpContent: t.helpContent,
        procedures: convertInternalLinks(t.procedures, docsIndex),
        relatedItems: t.relatedItems ?? [],
      }))[0];
  }

  const robotAlert = showRobotAlert(state) ? currentRobotAlert(state) : undefined;

  if (organization === undefined) {
    throw new Error('Illegal state (E121), organization not found');
  }

  const currentUserIsOwner = (currentEmployee?.email?.toUpperCase() ?? '') === organization.ownerEmail.toUpperCase();

  let defaultFunctionUuid = '';
  const defaultFunction = functionsNotDeleted(state).find((f) => {
    return f.templateId === 179; // Daglig leder
  });
  if (defaultFunction) {
    defaultFunctionUuid = defaultFunction.uuid;
  }

  const tutorialContent = currentTutorialContent(state);
  let currentUserHasMultipleOrganizations = false;
  if (state.user && state.user.organizations && state.user.organizations.length > 1) {
    currentUserHasMultipleOrganizations = true;
  }
  const headerStartTaskValue = currentStartTask(state);
  const allStartTasks = startTasks(state);
  const userStartTasks = allStartTasks.filter((item) => item.myTask);
  return {
    loaded: true,
    currentContentViewerContent: '',
    availableTutorials: availableTutorialsSelector(state),
    tutorialActive: isTutorialActive(organization, currentEmployee),
    displayedUserName: displayedUserName(state),
    pageMenu: getPageMenu(pages, organizationId, getFeatureStates(state), singleUser),
    currentOrganizationId: organizationId,
    currentOrganizationName: organizationName(state),
    currentEmployeeUuid: currentEmployeeUuid(state),
    currentUserEmail: getCurrentUserEmail(state) ?? '',
    currentEmployee: employeesNotDeleted(state).find((e) => {
      return e.uuid === currentEmployeeUuid(state);
    }),
    currentUserIsOwner,
    currentUserHasMultipleOrganizations,
    currentPageId: currentPageId,
    currentPageView: pageView(api, hrefPrefix, currentPageId, pages, state),
    currentPathArray: splitPath(state),
    headerStartTask: {
      startTaskExpanded: state.selectedStartTask !== '',
      startTask: headerStartTaskValue,
      relatedEvent: organization.eventOccurrences.find((e) => {
        return e.taskUuid === headerStartTaskValue?.uuid;
      }),
      userTotal: userStartTasks.length,
      userExecuted: userStartTasks.filter((item) => item.executed).length,
    },
    singleUserVersion: singleUser,
    helpViewerOpen: state.helpViewerOpen,
    tutorialViewerOpen: state.tutorialViewerOpen,
    currentTutorialContent: tutorialContent,
    currentHelpPage: state.currentHelpPage,
    sideContentOpen: state.sideContentOpen,
    copilotOpen: state.copilotOpen,
    chatOpen: state.chatOpen,
    chatMode: state.chatMode,
    chatExpanded: state.chatExpanded,
    matrixUnreadCount: state.matrixUnreadCount,
    currentSideContent: getCurrentSideContent(state),
    contentViews: await currentContentViews(api, hrefPrefix, pathSegments, state),
    employees: employeesNotDeleted(state).filter((e) => {
      // Basic filters
      if (e.status !== 'ACTIVE' || e.accessLevel === 'NONE') {
        return false;
      }

      // If user has read-only access and organization has ONLY performance review feature,
      // only show employees with write access (USER)
      const featureStates = getFeatureStates(state);
      const hasWriteAccess = writeAccess(state);

      // Check if this is a performance review-only account (no other features enabled)
      const isPerformanceReviewOnly =
        featureStates.performanceReview &&
        !featureStates.core &&
        !featureStates.hse &&
        !featureStates.meetings &&
        !featureStates.staffing &&
        !featureStates.assets &&
        !featureStates.substances &&
        !featureStates.issues &&
        !featureStates.risk &&
        !featureStates.data &&
        !featureStates.quality &&
        !featureStates.collections;

      if (!hasWriteAccess && isPerformanceReviewOnly) {
        return e.accessLevel === 'USER';
      }

      return true;
    }),
    employeesForShare: employeesNotDeleted(state)
      .filter((e) => {
        // Basic filters
        if (e.status !== 'ACTIVE' || e.accessLevel === 'NONE') {
          return false;
        }

        // If user has read-only access and organization has ONLY performance review feature,
        // only show employees with write access (USER)
        const featureStates = getFeatureStates(state);
        const hasWriteAccess = writeAccess(state);

        // Check if this is a performance review-only account (no other features enabled)
        const isPerformanceReviewOnly =
          featureStates.performanceReview &&
          !featureStates.core &&
          !featureStates.hse &&
          !featureStates.meetings &&
          !featureStates.staffing &&
          !featureStates.assets &&
          !featureStates.substances &&
          !featureStates.issues &&
          !featureStates.risk &&
          !featureStates.data &&
          !featureStates.quality &&
          !featureStates.collections;

        if (!hasWriteAccess && isPerformanceReviewOnly) {
          return e.accessLevel === 'USER';
        }

        return true;
      })
      .map((e) => ({ uuid: e.uuid, name: e.name, disabled: false })),
    contactsForShare: getContactsAsUsers(state)
      .filter((e) => e.accessLevel !== 'NONE')
      .map((e) => ({ uuid: e.uuid, name: e.name, disabled: false })),
    selectedEntityName: breadCrumbSelectedName(state),
    selectedEntityType: currentEntityTypeName(state),
    settings: getSettings(state, currentEmployee, currentUserIsOwner, organization),
    currentRobotAlert: robotAlert,
    functionsByTemplateId: functionsByTemplateId(state),
    featureStates: getFeatureStates(state),
    userEmails: getUsersForAccess(state).map((e) => e.email ?? ''),
    partners: partnersNotDeleted(state)
      .filter((p) => {
        return p.templateId === null || !p.contentLastModifiedWasCreate;
      })
      .map((p) => {
        return { value: p.uuid, text: p.name };
      }),
    defaultFunctionUuid,
    employeesCount: employeesNotTerminatedCount(state),
    specialTerms: getSpecialTerms(state),
    recipientOptionsForIssues: getRecipientOptionsForIssues(state),
    uiSettings: uiSettings(state),
    emergencyMeasuresCardsPublished: emergencyMeasuresCardsPublished(state),
    isUserEmergencyMode: isUserEmergencyMode(state),
    emergencyViewModel: getEmergencyViewModel(state),
    mobileAppViewModel: getMobileAppViewModel(state),
    lastOrganizationsLoadedByUser: lastOrganizationsLoadedByUser(state).slice(0, 5),
    currentUserHasEmergencyAccess: emergencyAccess(state),
  };
}

function _singleUserVersionAvailable(state: State) {
  return employeesNotDeleted(state).length === 1 && employeesDeleted(state).length === 0;
}

export async function applicationViewModel(api: ApiClient, state: State): Promise<ApplicationViewModel> {
  const l = loaded(state);

  if (l) {
    try {
      return await loadedApplicationViewModel(api, state);
    } catch (e) {
      console.error(e);
      return {
        loaded: false,
      };
    }
  } else {
    return {
      loaded: false,
    };
  }
}
